"use client";

import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useAppSelector, useAppDispatch } from "@/app/lib/hooks";
import { getTours, setFilter } from "@/app/lib/reducers/toursReducer";
import { getTimeSlots } from "@/app/lib/reducers/timeslotReducer";
import { getCityData } from "@/app/lib/reducers/citiesReducer";
import { useSearchParams, usePathname } from "next/navigation";
import { fetchExchangeRate } from "@/utils/currencyUtils";
import { useTranslation } from "react-i18next";

// `TypeOfExperiencesFilter` is intentionally NOT rendered any more — on the
// /city/[slug] redesign the CityCategoryAside drives the same
// `state.tours.filters.categories` slice (single source of truth for tour
// categories). Keeping it here too would mean two control surfaces fighting
// for the same value.
import PricePerPeopleFilter from "@/components/filters/PricePerPeopleFilter";
import PriceRangeFilter from "@/components/filters/PriceRangeFilter";
import TimeOfDayFilter from "@/components/filters/TimeOfDayFilter";
import DurationFilter from "@/components/filters/DurationFilter";
import SpecialFilters from "@/components/filters/SpecialFilter";

import ButtonPrimary from "@/shared/ButtonPrimary";
import ButtonThird from "@/shared/ButtonThird";
import ButtonClose from "@/shared/ButtonClose";

const TabFilters: React.FC<{
  origin?: string | null;
  /** `'horizontal'` (default) keeps the legacy row of pill buttons used at
   *  the top of /tours and other listings. `'vertical'` stacks the filters
   *  on top of each other — used by the /city/[slug] sidebar redesign. */
  layout?: "horizontal" | "vertical";
}> = ({ layout = "horizontal" }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("guydeez");

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const categorySlug = searchParams?.get("cat");
  const tourCategories = useAppSelector(state => state.tourCategories.tourCategories);

  const timeSlots = useAppSelector((state) => state.timeslot.timeSlots);
  const selectedCity = useAppSelector((state) => state.cities.currentCityData);
  const filters = useAppSelector((state) => state.tours.filters);
  const filtersTours = useAppSelector((state) => state.tours);
  const selectedLanguage = useAppSelector((state) => state.language.selectedLanguage || "en_US");
  const selectedCurrency = useAppSelector((state) => state.currencies.selectedCurrency || "EUR");
  const currencies = useAppSelector((state) => state.currencies.currencies);

  const [isOpenMoreFilter, setIsOpenMoreFilter] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState("€");
  const [cityFilterApplied, setCityFilterApplied] = useState(false);
  const loading = useAppSelector(state => state.tours.loading);
  const isOnCityPage = pathname?.includes("/city/");
 
  // Extract citySlug from pathname
  const citySlug = useMemo(() => {
    return pathname?.includes("/city/") ? pathname.split("/city/")[1]?.split("/")[0] : null;
  }, [pathname]);

  // Initial setup
  useEffect(() => {
    dispatch(getTimeSlots());
  }, [dispatch]);

  // Set symbol on currency change
  useEffect(() => {
    const symbol = currencies.find((c) => c.code === selectedCurrency)?.symbol || "€";
    setCurrencySymbol(symbol);
  }, [currencies, selectedCurrency]);

  // Apply filter after city data is loaded.
  // When navigating between cities (e.g. /city/kyoto → home → /city/paris)
  // we must overwrite `filters.city` with the *new* selectedCity.id —
  // preserving a stale value silently leaks the previous city's id into the
  // next `/api/tours` call and the user sees the old city's tours.
  useEffect(() => {
    if (citySlug && selectedCity?.id && !cityFilterApplied && loading === false) {
      if (filters.city !== Number(selectedCity.id)) {
        dispatch(setFilter("city", Number(selectedCity.id)));
      }
      setCityFilterApplied(true);
    }
  }, [citySlug, selectedCity?.id, cityFilterApplied, dispatch, filters.city, loading]);

  // Reset the "applied" latch whenever the city actually changes so the
  // effect above runs again for the new city. Without this, navigating
  // kyoto → paris keeps `cityFilterApplied = true` from the kyoto session
  // and the new city's id never gets written into filters.
  const lastCityIdRef = useRef<string | number | null>(null);
  useEffect(() => {
    const id = selectedCity?.id ?? null;
    if (lastCityIdRef.current !== null && lastCityIdRef.current !== id) {
      setCityFilterApplied(false);
    }
    lastCityIdRef.current = id;
  }, [selectedCity?.id]);

  // City→city client navigation can leave Redux holding the PREVIOUS city:
  // Next reuses the cached city layout's `initialCity`, so `currentCityData`
  // never updates, the race-guard in the refetch effect below blocks forever
  // (selectedCity.slug !== citySlug), and the grid keeps the old city's tours.
  // If the store's city slug doesn't match the URL slug, force-fetch the right
  // city — that lands the new `currentCityData`, which cascades into the
  // filters + tours refetch. The ref dispatches at most once per slug.
  const syncedCitySlugRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOnCityPage || !citySlug) return;
    const storeSlug = (selectedCity as any)?.slug ?? null;
    if (
      storeSlug &&
      storeSlug !== citySlug &&
      syncedCitySlugRef.current !== citySlug
    ) {
      syncedCitySlugRef.current = citySlug;
      dispatch(getCityData(citySlug, selectedLanguage));
    } else if (storeSlug === citySlug) {
      syncedCitySlugRef.current = citySlug;
    }
  }, [citySlug, selectedCity, isOnCityPage, selectedLanguage, dispatch]);


  // Fetch tours
  const handleApplyFilters = async () => {
    closeModalMoreFilter();

    const convertToEUR = async (amount: number) => {
      if (selectedCurrency === "EUR") return amount;
      const rate = await fetchExchangeRate(selectedCurrency, "EUR");
      return rate ? amount * rate : amount;
    };

    const minPriceEUR = await convertToEUR(filters.minPrice || 0);
    const maxPriceEUR = await convertToEUR(filters.maxPrice || 1000);

    // On a city page the tours MUST follow the URL's city. `filters.city` is
    // synced into Redux by a separate, `loading`-gated effect that can lag a
    // render (or more) behind the navigation, so reading it here can fetch the
    // PREVIOUS city's tours — the grid keeps showing e.g. London on the New
    // York page. This was invisible on desktop (SSR `initialTours` populate the
    // grid directly) but broke mobile, where SSR skips tours and the grid
    // depends entirely on this client fetch. The race-guard in the effect that
    // calls this already blocks until `selectedCity.slug === citySlug`, so
    // `selectedCity.id` is guaranteed to be the URL's city: use it as the source
    // of truth for the `city` param instead of the laggy filter.
    if (isOnCityPage) {
      const cityIdForFetch = Number((selectedCity as any)?.id) || null;
      cityIdForFetch &&
        dispatch(
          getTours(
            filtersTours.currentPage,
            27,
            filters.categories,
            filters.people,
            minPriceEUR,
            maxPriceEUR,
            cityIdForFetch,
            selectedLanguage,
            filters.startAt,
            filters.duration,
            filters.specialFilters
          )
        );
    } else {
      dispatch(
        getTours(
          filtersTours.currentPage,
          27,
          filters.categories,
          filters.people,
          minPriceEUR,
          maxPriceEUR,
          filters.city,
          selectedLanguage,
          filters.startAt,
          filters.duration,
          filters.specialFilters
        )
      );
    }
  };

  // Mobile city→city navigation refresh. The moment the resolved city matches
  // the URL slug, fire getTours for it — immediately and exactly once per city.
  // This is independent of the laggy `filters.city` and of the debounced effect
  // below (which can be starved by rapid re-renders during a navigation).
  // Desktop is covered by SSR `initialTours`; mobile ships none, so without this
  // the experiences grid keeps showing the previous city's tours.
  const fetchedCityIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isOnCityPage || !citySlug) return;
    const cityId = Number((selectedCity as any)?.id) || null;
    const slugMatches = (selectedCity as any)?.slug === citySlug;
    if (cityId && slugMatches && fetchedCityIdRef.current !== cityId) {
      fetchedCityIdRef.current = cityId;
      handleApplyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySlug, selectedCity, isOnCityPage]);

  // Apply filters after filters change (but only after city is set if needed)
  // First call on mount runs immediately to shorten LCP — the 300 ms debounce
  // is only useful for *subsequent* user-driven filter changes.
  const hasFiredFirstFetchRef = useRef(false);
  useEffect(() => {
    // Race-guard for client-side navigation between /city/* routes.
    //
    // When the user picks a new city in the mobile search sheet (or any
    // client-side router.push), `pathname` — and therefore `citySlug`
    // — flips to the new slug INSTANTLY, but `selectedCity` and
    // `filters.city` only catch up one render cycle later (CityLayout
    // dispatches setCityData / setFilter during its own render, which
    // doesn't propagate to TabFilters' useAppSelector reads until the
    // next render). The closure captured by this effect therefore still
    // holds the previous city's filters.city, and `handleApplyFilters`
    // would fire a `getTours` call for the OLD city. That stale
    // response can land after the correct follow-up request and ends
    // up overwriting the new city's tours in Redux — the user sees
    // Barcelona's tours on the Rennes page until next reload.
    //
    // We block the fetch until `selectedCity.slug` matches the URL.
    // Once Redux catches up, the effect re-fires (selectedCity is in
    // its deps) and the fetch runs with consistent state.
    if (
      citySlug &&
      (!selectedCity || (selectedCity as any).slug !== citySlug)
    ) {
      return;
    }

    const cityIdMatch = filters.city === Number(selectedCity?.id);

    const shouldApply =
      (!citySlug && cityIdMatch) ||
      (citySlug && cityFilterApplied && cityIdMatch) || !cityIdMatch;

    if (!shouldApply) return;

    if (!hasFiredFirstFetchRef.current) {
      hasFiredFirstFetchRef.current = true;
      handleApplyFilters();
      return;
    }

    const timeout = setTimeout(() => {
      handleApplyFilters();
    }, 300);

    return () => clearTimeout(timeout);
  }, [filters, selectedCity, citySlug]);

  // Initial load for non-city page
  useEffect(() => {

    if (categorySlug && tourCategories.length > 0) {
      const matchedCategory = tourCategories.find(cat => cat.slug === categorySlug);
      if (matchedCategory) {
        dispatch(setFilter("categories", [matchedCategory.uuid]));
      }
    }

    if (!citySlug) {
      handleApplyFilters();
    }

  }, []);

  // Set category filter from `cat` query param
useEffect(() => {
  if (categorySlug && tourCategories.length > 0) {
    const matchedCategory = tourCategories.find(cat => cat.slug === categorySlug);
    if (matchedCategory) {
      dispatch(setFilter("categories", [matchedCategory.uuid]));
    }
  }
}, [categorySlug, tourCategories, dispatch]);

  const closeModalMoreFilter = () => setIsOpenMoreFilter(false);
  const openModalMoreFilter = () => setIsOpenMoreFilter(true);

  const isVertical = layout === "vertical";
  return (
    <div className={isVertical ? "" : "flex lg:space-x-4"}>
      <div
        className={
          isVertical
            ? "flex flex-col gap-2"
            : "hidden lg:flex space-x-4"
        }
      >
        <PricePerPeopleFilter />
        <PriceRangeFilter />
        <TimeOfDayFilter />
        <DurationFilter />
        <SpecialFilters />
      </div>
    </div>
  );
};

export default TabFilters;
